import { useEffect, useState } from "react";
import { useSocket } from "@/app/components/socket/SocketProvider";
import ReminderItems, { LabReportData, type ReminderItemData, type VoucherGroupData } from "./ReminderItems";
import BillingMirrorHeader from "../BillingMirrorHeader";
import { useUtils } from "@/app/hooks/useUtils";

type RemindersPayload = {
  petName?: string | null;
  doctorName?: string | null;
  reminders?: ReminderItemData[];
  labReport?:LabReportData[];
  voucherGroups?: VoucherGroupData[];
};

const resolveEventType = (message: unknown) => {
  if (!message || typeof message !== "object") return null;
  const anyMessage = message as any;
  return anyMessage.event ?? anyMessage.type ?? anyMessage.event_type ?? null;
};

const asStringOrNull = (value: unknown) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
};

const normalizeReminderItem = (raw: unknown): ReminderItemData | null => {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as any;
  const title =
    asStringOrNull(item.title) ??
    asStringOrNull(item.reminder) ??
    asStringOrNull(item.name) ??
    asStringOrNull(item.label);
  if (!title) return null;

  const dueDate =
    asStringOrNull(item.due_date) ??
    asStringOrNull(item.dueDate) ??
    asStringOrNull(item.due_on) ??
    asStringOrNull(item.dueOn) ??
    asStringOrNull(item.date) ??
    asStringOrNull(item.due) ??
    null;

  return { title, dueDate };
};



const normalizeRemindersPayload = (payload: unknown): RemindersPayload => {
  if (!payload || typeof payload !== "object") return {};
  const data = payload as any;

  const voucherGroups = Array.isArray(data.voucher_groups) ? data.voucher_groups.filter((voucherGroup: any) => voucherGroup?.group) : [];

  const pet = Array.isArray(data.pets) ? data.pets[0] : null;
  if (!pet) return { voucherGroups };

  const petName =
    asStringOrNull(pet.pet_name) ??
    asStringOrNull(pet.petName) ??
    null;

  const doctorName =
    asStringOrNull(pet.doctor_name) ??
    asStringOrNull(pet.doctorName) ??
    null;

  const doctorList = Array.isArray(pet.DOCTOR) ? pet.DOCTOR : [];
  const vaccineList = Array.isArray(pet.VACCINE) ? pet.VACCINE : [];
  const labReportList=Array.isArray(pet.LAB_REPORTS) ? pet.LAB_REPORTS : [];

  const labReport=labReportList.map((r: any) => ({
      title: r.lab_report_name,
      tat_date: r.consumer_tat_date ?? r.tat_date,
    }))
    .filter(r => r.title);

  const merged = [...doctorList, ...vaccineList];

  const reminders = merged
    .map((r: any) => ({
      title: r.reminder_name,
      dueDate: r.due_date,
    }))
    .filter(r => r.title);

  return { petName, doctorName, reminders, labReport, voucherGroups };
};


const ReminderComponent = ({
  mirrorCustomerId,
  mirrorCartId,
  mappedClinicName
}: {
  mirrorCustomerId?: string | null;
  mirrorCartId?: string | null;
  mappedClinicName?:string|null;
}) => {

  const { lastJsonMessage: socketJsonMessage, lastMessage: socketRawMessage } = useSocket();
  const [payload, setPayload] = useState<RemindersPayload>({});
  const {veticDateFormat} = useUtils();

 const handleRemindersMessage = (message: any) => {
  if (!message || typeof message !== "object") return false;

  const eventType =
    message.event ??
    message.type ??
    message.event_type ??
    null;

  if (!eventType || String(eventType).toLowerCase() !== "reminders") return false;
  setPayload(normalizeRemindersPayload(message));

  return true;
};

  useEffect(() => {
    if (handleRemindersMessage(socketJsonMessage)) return;

    const data = socketRawMessage?.data;
    if (!data) return;

    const parseText = (rawText: string) => {
      const raw = rawText.trim();
      if (!raw || raw === "pong") return;
      try {
        const parsed = JSON.parse(raw);
        handleRemindersMessage(parsed);
      } catch {
        return;
      }
    };

    if (typeof data === "string") {
      parseText(data);
      return;
    }

    if (data instanceof Blob) {
      data.text().then(parseText).catch((error) => {
        console.error("[ReminderMessage] Failed to parse blob:", error);
      });
      return;
    }

    if (data instanceof ArrayBuffer) {
      parseText(new TextDecoder().decode(data));
      return;
    }

    if (ArrayBuffer.isView(data)) {
      parseText(new TextDecoder().decode(data));
    }
  }, [socketJsonMessage, socketRawMessage, mirrorCustomerId, mirrorCartId]);

  return (
    <div className="bg-gradient-to-br from-orange-50 via-blue-50 to-green-50 p-4 h-[var(--mirror-frame-height,100vh)] overflow-y-auto">
   <BillingMirrorHeader clinicName={mappedClinicName} />
      <ReminderItems petName={payload.petName} doctorName={payload.doctorName} reminders={payload.reminders} labreport={payload.labReport} voucherGroups={payload.voucherGroups}/>
    </div>
  );
};

export default ReminderComponent;
